import { useEffect, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import PaymentStats from "./Content/PaymentStats";
import { myJobsPageStyles } from "./styles/styles";
import { JobsContent } from "./Content/JobsContent";
import CustomFilterTabs from "../../Components/CustomFilterTabs";
import useJobsData from "./hooks/useJobsData";
import { useTranslation } from "react-i18next";

const MyJobsContainer = () => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const containerRef = useRef(null);
  const [activeTab, setActiveTab] = useState("pending");
  const { t } = useTranslation("page-my-jobs");

  const prevActiveTabRef = useRef(activeTab);

  const {
    pendingRequestsJobsData,
    activeJobsData,
    completedJobsData,
    missedJobsData,

    isPendingJobsLoading,
    isJobsLoading,

    fetchPendingJobs,
    fetchActiveJobs,
    fetchCompletedJobs,
    fetchMissedJobs,
    fetchJobsList,

    catalogTotals,

    hasMoreFor,
    loadMoreFor,
  } = useJobsData();

  const { mainContainerStyles, scrollableContainerStyles } = myJobsPageStyles;

  // Tab counts are the TRUE totals from the API, not how many rows happen to be
  // loaded — with pagination those diverge as you scroll, and a count that
  // climbs while you read is worse than no count. They cover every order type:
  // New Jobs is already merged; the other lists are service-only, so the
  // store + restaurant totals are added on top.
  const countOf = (list) => list?.total_count ?? list?.data?.length ?? 0;
  const pendingRequestsCount = countOf(pendingRequestsJobsData);
  const activeJobsCount = countOf(activeJobsData) + catalogTotals.active;
  const completedJobsCount = countOf(completedJobsData) + catalogTotals.completed;
  const missedJobsCount = countOf(missedJobsData) + catalogTotals.missed;

  const handleTabChange = (_, newValue) => setActiveTab(newValue);

  useEffect(() => {
    if (!isMobile) return;

    if (prevActiveTabRef.current !== activeTab) {
      if (activeTab === "pending") {
        fetchPendingJobs(isMobile);
      } else if (activeTab === "active") {
        fetchActiveJobs();
      } else if (activeTab === "completed") {
        fetchCompletedJobs();
      } else if (activeTab === "missed") {
        fetchMissedJobs();
      }
    }

    prevActiveTabRef.current = activeTab;
  }, [
    activeTab,
    isMobile,
    fetchPendingJobs,
    fetchActiveJobs,
    fetchCompletedJobs,
    fetchMissedJobs,
  ]);

  return (
    <Box sx={mainContainerStyles}>
      <PaymentStats />

      <Box sx={{ display: isMobile ? "flex" : "none" }}>
        <CustomFilterTabs
          tabs={[
            {
              label: t("pending_requests"),
              count: pendingRequestsCount,
              value: "pending",
            },
            {
              label: t("jobs_in_progress"),
              count: activeJobsCount,
              value: "active",
            },
            {
              label: t("completed_jobs"),
              count: completedJobsCount,
              value: "completed",
            },
            {
              label: t("missed_jobs"),
              count: missedJobsCount,
              value: "missed",
            },
          ]}
          activeTab={activeTab}
          onChange={handleTabChange}
        />
      </Box>

      <Box
        sx={scrollableContainerStyles}
        ref={containerRef}
      >
        <JobsContent
          {...{
            containerRef,
            activeTab,
            pendingRequestsJobsData: pendingRequestsJobsData?.data || [],
            activeJobsData: activeJobsData?.data || [],
            completedJobsData: completedJobsData?.data || [],
            missedJobsData: missedJobsData?.data || [],

            isPendingJobsLoading,
            isJobsLoading,

            fetchPendingJobs,
            fetchActiveJobs,
            fetchCompletedJobs,
            fetchMissedJobs,

            hasMoreFor,
            loadMoreFor,
            fetchJobsList,
          }}
        />
      </Box>
    </Box>
  );
};

export default MyJobsContainer;
